import mixins from 'vue-typed-mixins';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { ErrorResponse } from '@codequality/vue-cli-base';

import { E入力分類, Eフェーズ種別 } from '@/openapi/enum';
import { データApi, データRequest, 勘定科目Api, 部門Api } from '@/openapi/user';

import C決算期TableHeader from '@/components/決算期TableHeader/決算期TableHeader.vue';
import MoneyUnitSelect from '@/components/moneyUnitSelect/MoneyUnitSelect.vue';
import InputableText from '@/components/inputableText/InputableText.vue';

import AlertMixin from '@/mixins/AlertMixin';
import ListMixin from '@/entries/user/mixins/ListMixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';
import { InputableTextMixin } from '@/components/inputableText/InputableText';
import InputableTextBaseMixin from '@/entries/user/mixins/InputableTextBaseMixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import 実態純資産Service, { 実態純資産, 実態純資産ListItem } from '@/classes/service/計画/実態純資産Service';

export default mixins(
  AlertMixin,
  ListMixin,
  RoutesMixin,
  計画書Mixin,
  事業所Mixin,
  InputableTextMixin,
  InputableTextBaseMixin
).extend({
  name: 'P実態純資産',

  components: {
    C決算期TableHeader,
    MoneyUnitSelect,
    InputableText,
  },

  data(): {
    api: {
      d: データApi;
      k: 勘定科目Api;
      b: 部門Api;
    };
    service: 実態純資産Service;
    実態純資産: 実態純資産 | null;
    editItem: 実態純資産ListItem;
  } {
    return {
      api: {
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
        b: UserApiUtility.ogApi(部門Api),
      },
      service: new 実態純資産Service(Eフェーズ種別.計画フェーズ),
      実態純資産: null,
      editItem: {
        番号: 0,
        label: '',
        values: [],
      },
    };
  },

  computed: {
    canUpdate(): boolean {
      return this.editItem.データpk !== undefined;
    },
    maxNo(): number {
      if (this.実態純資産 === null || this.実態純資産.list.length === 0) return 0;
      return Math.max(...this.実態純資産.list.map((listItem) => listItem.番号));
    },
  },

  async created(): Promise<void> {
    await this.$_load();
  },

  methods: {
    selectForEdit(item: 実態純資産ListItem): void {
      this.editItem = _.cloneDeep(item);
    },
    clearForm(): void {
      this.editItem = {
        番号: 0,
        label: '',
        values: new Array(this.実態純資産!.header.length - 1).fill(0),
      };
    },
    async save(isNew: boolean): Promise<void> {
      if (this.editItem.values.filter((v) => v === undefined).length > 0) {
        alert('数値を入力してください。');
        return;
      }
      const { データpk, 番号, ...cデータ実態純資産 } = this.editItem;
      const c番号 = isNew ? this.maxNo + 1 : 番号;
      const request: データRequest = {
        c入力分類: E入力分類.実態純資産,
        c番号,
        cフェーズ種別: Eフェーズ種別.計画フェーズ,
        cデータ: JSON.stringify(cデータ実態純資産),
      };
      const response: AxiosResponse<void | ErrorResponse> = isNew
        ? await this.api.d.createデータ(this.計画書.pk, request)
        : await this.api.d.updateデータ(データpk!, request);

      if (this.isValidationErrorResponse(response)) return;
      this.$_load();
    },
    async remove(データpk: number): Promise<void> {
      const isCancelOrError = await this.isCancelOrValidationErrorResponseToDelete(() =>
        this.api.d.deleteデータ(データpk)
      );
      if (isCancelOrError) return;
      this.$_load();
    },
    async $_load(): Promise<void> {
      // 勘定科目一覧
      const 勘定科目Options = { params: { sj: 'PLBSCF_計画' } };
      const 勘定科目Request = this.api.k.list勘定科目(this.事業所.pk, undefined, undefined, undefined, 勘定科目Options);
      // 部門一覧
      const 部門Request = this.api.b.list部門(this.事業所.pk);
      // データ一覧
      const データOptions = { params: { sj: 'PLBSCF' } };
      const データRequest = this.api.d.listデータ(
        this.計画書.pk,
        this.service.getRelationalE入力分類s(),
        undefined,
        データOptions
      );
      // API呼び出し
      const responses = await Promise.all([勘定科目Request, 部門Request, データRequest]);
      this.service.set勘定科目s = responses[0].data;
      this.service.set部門s = responses[1].data;
      this.実態純資産 = this.service.parse(responses[2].data);
      // input初期化
      this.clearForm();
    },
    changeCell(value: string | number, row: 実態純資産ListItem, i: number): void {
      this.$set(row.values, i, Number(value));
    },
  },
});
